import sha1 from 'js-sha1';
import store from 'lib/store';
import { webpushConfig } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import { type Account, getAccounts, getActiveAccount } from 'reactApp/modules/ph/ph.helpers';
import { sendXray } from 'reactApp/utils/ga';

import { type Provider, FEATURE_NAME, log } from './shared';
import { getActiveSwRegistration } from './webPush.sw';

export function isTokenSentToServer(hash: string) {
    return store.get(`${FEATURE_NAME}|sentToServer`) === hash;
}

export function setTokenSentToServer(sent: string | false) {
    store.set(`${FEATURE_NAME}|sentToServer`, sent);
}

/** Сырое значение кэша отправки — для диагностики. */
export function getSentToServerValue(): unknown {
    return store.get(`${FEATURE_NAME}|sentToServer`);
}

export function getStoredProvider(): Provider | undefined {
    return store.get(`${FEATURE_NAME}|provider`) as Provider | undefined;
}

export function setStoredProvider(provider: Provider) {
    store.set(`${FEATURE_NAME}|provider`, provider);
}

/**
 * Техническая аналитика (xray) по факту УСПЕШНОЙ отправки регистрации на сервер:
 * - первичная подписка (маркера ещё нет) -> `web-push_subscribe_<provider>`;
 * - ре-регистрация тем же провайдером (смена состава аккаунтов / ротация endpoint)
 *   -> `web-push_resubscribe_<provider>` — чтобы не завышать метрику первичных;
 * - смена провайдера (cutover) -> `web-push_switch_<prev>_to_<provider>`.
 * Вызывать ДО setStoredProvider — getStoredProvider ещё держит прежнего провайдера
 * (маркер только выставляется, никогда не очищается, поэтому undefined = не подписывались).
 */
export function trackSubscriptionSent(provider: Provider) {
    const prev = getStoredProvider();

    log(`Track subscription: storedProvider=${prev ?? 'none'}, currentProvider=${provider}`);

    if (!prev) {
        log(`Analytics -> subscribe_${provider} (первичная подписка, маркера провайдера не было)`);
        sendXray([FEATURE_NAME, 'subscribe', provider]);
    } else if (prev === provider) {
        log(`Analytics -> resubscribe_${provider} (ре-регистрация тем же провайдером)`);
        sendXray([FEATURE_NAME, 'resubscribe', provider]);
    } else {
        log(
            `Analytics -> switch_${prev}_to_${provider} (смена провайдера; прежний маркер=${prev} — например остаток от прошлого тестирования)`
        );
        sendXray([FEATURE_NAME, 'switch', prev, 'to', provider]);
    }
}

/**
 * Единый источник аккаунтов для дедупа/аналитики web-push. multiacc=false → только
 * активный аккаунт: тогда ключ (sha1 по email'ам) меняется при смене активного аккаунта →
 * реальная переотправка регистрации → летит web-push_resubscribe_<provider>.
 * multiacc=true → весь набор мультиавторизации.
 * Активного аккаунта может не быть (напр. __PH ещё не готов → getAccounts() вернул []) —
 * тогда отдаём пустой набор, а не [undefined], чтобы хеши/отправка не падали на account.email.
 * Держим здесь, рядом с getEmailsHash/getSubscriptionHash, чтобы отправка, дедуп и
 * диагностика считались по одному и тому же набору аккаунтов.
 */
export async function getWebPushAccounts(): Promise<Account[]> {
    const accounts = webpushConfig.multiacc
        ? await getAccounts()
        : [await getActiveAccount()].filter((account): account is Account => Boolean(account));

    log('accounts', accounts);

    return accounts;
}

export function getEmailsHash(accounts: Account[]): string {
    return sha1(accounts.map((acc) => acc.email).join());
}

// Ключ дедупа pushme-подписки: endpoint уникален для подписки, а список аккаунтов
// может измениться (мультиавторизация) — тогда нужно перерегистрировать.
export function getSubscriptionHash(endpoint: string, accounts: Account[]): string {
    return sha1(endpoint + accounts.map((account) => account.email).join());
}

// Хеш уже отправленной pushme-подписки (или undefined, если подписки нет / SW недоступен).
// Нужен, чтобы fast-path в checkNotificationState сверял тот же ключ, что пишет отправка.
async function getSentPushmeHash(accounts: Account[]): Promise<string | undefined> {
    try {
        const reg = await getActiveSwRegistration();
        const sub = await reg.pushManager.getSubscription();

        return sub ? getSubscriptionHash(sub.toJSON().endpoint ?? '', accounts) : undefined;
    } catch (error) {
        log('Unable to read push subscription. ', error);
        return undefined;
    }
}

// Ключ отправленной подписки для текущего провайдера — тем же способом, что пишет отправка.
// fcm: sha1(emails); pushme: sha1(endpoint + emails) из активной подписки (или undefined).
export async function getSentSubscriptionKey(accounts: Account[]): Promise<string | undefined> {
    return webpushConfig.provider === 'pushme' ? getSentPushmeHash(accounts) : getEmailsHash(accounts);
}
